import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Circle, Clock } from 'lucide-react';

const UserDashboard = () => {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await api.get('/api/progress/me');
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStageIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={24} style={{ color: 'var(--accent-green-200)' }} />;
    if (status === 'in_progress') return <Clock size={24} style={{ color: 'var(--accent-orange-400)' }} />;
    return <Circle size={24} style={{ color: 'var(--text-muted)' }} />;
  };

  const getStageColor = (status) => {
    if (status === 'completed') return 'var(--accent-green-200)';
    if (status === 'in_progress') return 'var(--accent-orange-200)';
    return 'var(--bg-card)';
  };

  const getStatusText = (status) => {
    const texts = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed'
    };
    return texts[status] || status;
  };

  const agentNames = {
    lead_generation: 'Agent #1: Lead Generation & Prospecting',
    qualification: 'Agent #2: Lead Qualification & Needs Assessment',
    demo_poc: 'Agent #3: Demo & Proof-of-Concept Manager',
    contract_onboarding: 'Agent #4: Contract & Onboarding + Training',
    project_management: 'Agent #5: Project Management & Communication',
    technical_support: 'Agent #6: Technical Support & Optimization'
  };

  const getPlanInfo = () => {
    const plans = {
      starter: { name: 'Starter', color: 'var(--accent-blue-400)', price: '$4,997' },
      professional: { name: 'Professional', color: 'var(--accent-orange-400)', price: '$12,997' },
      enterprise: { name: 'Enterprise', color: 'var(--accent-purple-400)', price: '$29,997' }
    };
    return plans[user?.plan_type] || { name: 'No Plan', color: 'var(--text-muted)', price: '-' };
  };

  const planInfo = getPlanInfo();
  const completedStages = progress?.stages?.filter(s => s.status === 'completed').length || 0;
  const totalStages = 6;
  const progressPercentage = (completedStages / totalStages) * 100;

  return (
    <DashboardLayout title="My Dashboard">
      {/* Plan Info */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="voice-card" style={{ background: planInfo.color }}>
          <div className="heading-3 mb-2 text-white">{planInfo.name} Plan</div>
          <div className="heading-2 text-white">{planInfo.price}</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-blue-200)' }}>
          <div className="body-small mb-2">Overall Progress</div>
          <div className="heading-2">{completedStages} / {totalStages}</div>
          <div className="w-full bg-white rounded-full h-2 mt-2">
            <div className="h-2 rounded-full" style={{ width: `${progressPercentage}%`, background: 'var(--accent-green-200)' }}></div>
          </div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-green-200)' }}>
          <div className="body-small mb-2">Account Status</div>
          <div className="heading-3">{user?.is_active ? 'Active' : 'Inactive'}</div>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="voice-card mb-8" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <h2 className="heading-2 mb-2">Welcome, {user?.full_name}!</h2>
        <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
          Track your AI implementation progress below. Our team is working through each stage to get your system up and running.
        </p>
      </div>

      {/* Implementation Progress */}
      <div className="voice-card">
        <h2 className="heading-3 mb-6">Implementation Progress - 6 Agent System</h2>
        
        {loading ? (
          <p>Loading progress...</p>
        ) : !progress ? (
          <div className="text-center py-8">
            <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>No implementation started yet.</p>
            <p className="body-medium" style={{ color: 'var(--text-muted)' }}>Your progress will appear here once your project begins.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {progress.stages.map((stage, index) => (
              <div
                key={stage.stage}
                className="p-6 rounded-lg border-l-4"
                style={{ 
                  background: getStageColor(stage.status),
                  borderLeftColor: stage.status === 'completed' ? 'var(--accent-green-200)' : 
                                   stage.status === 'in_progress' ? 'var(--accent-orange-400)' : 
                                   'var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {getStageIcon(stage.status)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="voice-card-title mb-1">{agentNames[stage.stage]}</h3>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ 
                          background: stage.status === 'completed' ? 'var(--accent-green-200)' : 
                                     stage.status === 'in_progress' ? 'var(--accent-orange-400)' : 
                                     'var(--bg-card)',
                          color: stage.status === 'not_started' ? 'var(--text-muted)' : 'white'
                        }}>
                          {getStatusText(stage.status)}
                        </span>
                      </div>
                    </div>
                    
                    {stage.notes && (
                      <p className="body-small mt-2" style={{ color: 'var(--text-secondary)' }}>
                        {stage.notes}
                      </p>
                    )}
                    
                    {stage.started_at && (
                      <div className="mt-2 caption" style={{ color: 'var(--text-muted)' }}>
                        Started: {new Date(stage.started_at).toLocaleDateString()}
                        {stage.completed_at && ` • Completed: ${new Date(stage.completed_at).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Support */}
      <div className="voice-card mt-8" style={{ background: 'var(--accent-blue-200)' }}>
        <h3 className="heading-3 mb-2">Need Help?</h3>
        <p className="body-medium mb-4">Our team is here to support you throughout your implementation.</p>
        <a href="mailto:support@aithatwows.com" className="btn-primary inline-block">
          Contact Support
        </a>
      </div>
    </DashboardLayout>
  );
};

export default UserDashboard;